import type { ReportHistoryListItem } from '../api/reportHistory';
import { PIPEFITTER_REPORT_TYPES } from '../constants/reportVersionTypes';
import { normalizePipeIdSetKey, pipeIdSetsMatch } from './reportHistoryGroups';

const IN_PROGRESS_STATUSES = new Set([
  'started',
  'processing',
  'inprogress',
  'in progress',
]);

export type PipefitterReportTypeKey =
  | typeof PIPEFITTER_REPORT_TYPES.PIPE_DATA
  | typeof PIPEFITTER_REPORT_TYPES.PIPE_COMPATIBILITY;

export type PendingReportGeneration = {
  reportType: PipefitterReportTypeKey;
  pipeIdsKey: string;
  startedAtMs: number;
};

function historyTimestamp(row: ReportHistoryListItem): number {
  const value = row.generatedAt;
  if (value instanceof Date) return value.getTime();
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function isReportHistoryInProgress(status: string | null | undefined): boolean {
  if (!status) return false;
  return IN_PROGRESS_STATUSES.has(status.trim().toLowerCase());
}

export function hasAnyInProgressReportHistory(histories: ReportHistoryListItem[]): boolean {
  return histories.some((row) => isReportHistoryInProgress(row.status));
}

function matchingHistories(
  histories: ReportHistoryListItem[],
  reportType: PipefitterReportTypeKey,
  pipeIds: string[]
): ReportHistoryListItem[] {
  return histories.filter(
    (row) =>
      row.reportType === reportType &&
      pipeIdSetsMatch(row.pipeIds ?? [], pipeIds)
  );
}

/** History rows created for the current generation attempt (ignore older completed rows). */
export function recentMatchingHistories(
  histories: ReportHistoryListItem[],
  reportType: PipefitterReportTypeKey,
  pipeIds: string[],
  startedAtMs: number
): ReportHistoryListItem[] {
  const graceMs = 15_000;
  return matchingHistories(histories, reportType, pipeIds).filter(
    (row) => historyTimestamp(row) >= startedAtMs - graceMs
  );
}

export function isGroupReportGenerating(
  pipeIds: string[],
  reportType: PipefitterReportTypeKey,
  histories: ReportHistoryListItem[],
  pendingGenerations: PendingReportGeneration[]
): boolean {
  if (!pipeIds.length) return false;

  const pipeIdsKey = normalizePipeIdSetKey(pipeIds);
  const pending = pendingGenerations.find(
    (entry) => entry.reportType === reportType && entry.pipeIdsKey === pipeIdsKey
  );

  if (pending) {
    const recent = recentMatchingHistories(
      histories,
      reportType,
      pipeIds,
      pending.startedAtMs
    );
    if (recent.length === 0) {
      return true;
    }
    return recent.some((row) => isReportHistoryInProgress(row.status));
  }

  return matchingHistories(histories, reportType, pipeIds).some((row) =>
    isReportHistoryInProgress(row.status)
  );
}

export function prunePendingGenerations(
  pending: PendingReportGeneration[],
  histories: ReportHistoryListItem[]
): PendingReportGeneration[] {
  return pending.filter((entry) => {
    const pipeIds = entry.pipeIdsKey.split(',').filter(Boolean);
    const recent = recentMatchingHistories(
      histories,
      entry.reportType,
      pipeIds,
      entry.startedAtMs
    );
    return recent.length === 0;
  });
}

export function hasActiveReportGenerations(
  histories: ReportHistoryListItem[],
  pendingGenerations: PendingReportGeneration[]
): boolean {
  if (hasAnyInProgressReportHistory(histories)) {
    return true;
  }

  return pendingGenerations.some((entry) => {
    const pipeIds = entry.pipeIdsKey.split(',').filter(Boolean);
    const recent = recentMatchingHistories(
      histories,
      entry.reportType,
      pipeIds,
      entry.startedAtMs
    );
    if (recent.length === 0) {
      return true;
    }
    return recent.some((row) => isReportHistoryInProgress(row.status));
  });
}

export function createPendingReportGeneration(
  reportType: PipefitterReportTypeKey,
  pipeStatusIds: string[]
): PendingReportGeneration | null {
  const pipeIdsKey = normalizePipeIdSetKey(pipeStatusIds);
  if (!pipeIdsKey) return null;
  return {
    reportType,
    pipeIdsKey,
    startedAtMs: Date.now(),
  };
}
